import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Ban, Save, ShieldAlert, ShieldCheck, X } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../ui/Toast';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { checkboxClass } from '../../components/ui/Input';

/**
 * WP-9 (Part C) — per-account permission overrides + toxic acknowledgments.
 *
 * The screen where C4 bites. The server is the authority: this drawer only shows what
 * `GET /admin/users/:id/permissions` reports and forwards the save, so a refusal
 * (422 TOXIC_COMBINATION / DEPENDENCY_UNMET) surfaces exactly as the endpoint decided it —
 * it is never pre-empted here. Hiding a checkbox is not access control.
 */

interface Override {
  action: string;
  effect: 'GRANT' | 'REVOKE';
  reason: string | null;
}
interface TrippedPair {
  pairKey: string;
  acknowledged: boolean;
  rationale: string;
}
interface Authority {
  userId: string;
  role: string;
  overrides: Override[];
  actions: string[];
  roleActions: string[];
  trippedPairs: TrippedPair[];
}
interface CatalogueEntry {
  action: string;
  zone: string;
  requires: string[];
  covers: string[];
}
interface Catalogue {
  size: number;
  actions: CatalogueEntry[];
  systemActions: string[];
  toxicPairs: { key: string; left: string; right: string; rationale: string }[];
}
interface Ack {
  id: string;
  scope: 'USER' | 'ROLE';
  user_id: string | null;
  role: string | null;
  pair_key: string;
  reason: string;
  granted_by: string | null;
}

const cid = (): string => crypto.randomUUID();
/** Toxic pair key → i18n key (adminScreens.permissions.pairs.*); unknown keys show the raw key. */
const PAIR_LABEL_KEYS = [
  'SELLER_VALIDATES_OWN_CASH',
  'SELF_APPROVED_DISCOUNT',
  'SELF_APPROVED_PURCHASE',
  'RETURN_APPROVER_RECEIVES',
  'COLLECTOR_WRITES_OFF',
] as const;

export function PermissionsDrawer({
  userId,
  userLabel,
  isOwner,
  onClose,
}: {
  userId: string;
  userLabel: string;
  /** Is the SIGNED-IN user an OWNER? Only an OWNER may acknowledge a pair (server-enforced). */
  isOwner: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const pairLabel = (key: string): string =>
    (PAIR_LABEL_KEYS as readonly string[]).includes(key)
      ? t(`adminScreens.permissions.pairs.${key}`)
      : key;
  const [authority, setAuthority] = useState<Authority | null>(null);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [acks, setAcks] = useState<Ack[]>([]);
  const [draft, setDraft] = useState<Record<string, 'GRANT' | 'REVOKE'>>({});
  const [busy, setBusy] = useState(false);
  const [ackReason, setAckReason] = useState('');
  const [ackPair, setAckPair] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    try {
      const [a, c, k] = await Promise.all([
        apiClient.get<{ data: Authority }>(`/admin/users/${userId}/permissions`),
        apiClient.get<{ data: Catalogue }>('/admin/action-catalogue'),
        // Unfiltered: an account is covered by its OWN acknowledgments *and* by any
        // role-scoped one (that is how the C3 reference matrix ships saveable).
        apiClient.get<{ data: Ack[] }>('/admin/toxic-acknowledgments'),
      ]);
      setAuthority(a.data.data);
      setCatalogue(c.data.data);
      setAcks(k.data.data);
      setDraft(Object.fromEntries(a.data.data.overrides.map((o) => [o.action, o.effect])));
    } catch (err) {
      showApiError(err);
    }
  };

  useEffect(() => {
    void load();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const byZone = useMemo(() => {
    const map = new Map<string, CatalogueEntry[]>();
    for (const e of catalogue?.actions ?? []) {
      map.set(e.zone, [...(map.get(e.zone) ?? []), e]);
    }
    return [...map.entries()];
  }, [catalogue]);

  const roleHas = (a: string): boolean => authority?.roleActions.includes(a) ?? false;
  /** Effective = role ± the draft overrides — what the save WOULD produce. */
  const effective = (a: string): boolean =>
    draft[a] === 'GRANT' ? true : draft[a] === 'REVOKE' ? false : roleHas(a);

  /**
   * The pairs the DRAFT would produce — not just the ones already saved. Without this you
   * could never acknowledge a combination, because the refusal keeps it from ever being
   * saved in the first place: the tick is refused, the server's `trippedPairs` stays empty,
   * and there is nothing on screen to acknowledge. Presentation only — the server re-derives
   * all of it on save and is the one that refuses.
   */
  const livePairs = useMemo(() => {
    if (!authority || authority.role === 'OWNER') return []; // OWNER alone is exempt (C4)
    const savedByKey = new Map(authority.trippedPairs.map((p) => [p.pairKey, p]));
    return (catalogue?.toxicPairs ?? [])
      .filter((p) => effective(p.left) && effective(p.right))
      .map((p) => {
        const ack = acks.find(
          (a) =>
            a.pair_key === p.key &&
            ((a.scope === 'USER' && a.user_id === userId) ||
              (a.scope === 'ROLE' && a.role === authority.role))
        );
        return {
          pairKey: p.key,
          left: p.left,
          right: p.right,
          rationale: p.rationale,
          acknowledged: savedByKey.get(p.key)?.acknowledged ?? !!ack,
          /** true → this pair comes from unsaved ticks, so the save WILL be refused. */
          unsaved: !savedByKey.has(p.key),
          ack: ack && ack.scope === 'USER' ? ack : undefined,
          roleWide: ack?.scope === 'ROLE',
        };
      });
  }, [authority, catalogue, acks, draft, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (a: string): void => {
    setDraft((d) => {
      const next = { ...d };
      // role holds it → the only meaningful override is REVOKE, and vice versa.
      if (next[a]) delete next[a];
      else next[a] = roleHas(a) ? 'REVOKE' : 'GRANT';
      return next;
    });
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    try {
      const overrides = Object.entries(draft).map(([action, effect]) => ({ action, effect }));
      const { data } = await apiClient.put<{ data: Authority }>(
        `/admin/users/${userId}/permissions`,
        { overrides },
        { headers: { 'X-Command-Id': cid() } }
      );
      setAuthority(data.data);
      push({ tone: 'success', message: t('adminScreens.permissions.saved') });
    } catch (err) {
      // 422 TOXIC_COMBINATION / DEPENDENCY_UNMET land here with the server's own explanation.
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const grantAck = async (): Promise<void> => {
    if (!ackPair || ackReason.trim().length < 10) {
      push({
        tone: 'warning',
        message: t('adminScreens.permissions.ackReasonTooShort'),
      });
      return;
    }
    setBusy(true);
    try {
      await apiClient.post(
        '/admin/toxic-acknowledgments',
        { scope: 'USER', userId, pairKey: ackPair, reason: ackReason.trim() },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({ tone: 'success', message: t('adminScreens.permissions.ackGranted') });
      setAckPair(null);
      setAckReason('');
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const revokeAck = async (id: string): Promise<void> => {
    setBusy(true);
    try {
      await apiClient.post(
        `/admin/toxic-acknowledgments/${id}/revoke`,
        {},
        { headers: { 'X-Command-Id': cid() } }
      );
      push({ tone: 'success', message: t('adminScreens.permissions.ackRevoked') });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const dirty = useMemo(() => {
    const saved = new Map((authority?.overrides ?? []).map((o) => [o.action, o.effect]));
    const keys = new Set([...saved.keys(), ...Object.keys(draft)]);
    return [...keys].some((k) => saved.get(k) !== draft[k]);
  }, [authority, draft]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[82vh] w-full max-w-4xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {t('adminScreens.permissions.title', { name: userLabel })}
            </h2>
            <p className="text-xs text-muted-foreground">
              {authority
                ? t('adminScreens.permissions.summary', {
                    role: t(`adminScreens.users.roles.${authority.role}`, {
                      defaultValue: authority.role,
                    }),
                    count: authority.actions.length,
                    total: catalogue?.size ?? 47,
                  })
                : t('adminScreens.permissions.loading')}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* ---- separation of duties ------------------------------------------------ */}
          {livePairs.length > 0 && (
            <div
              id="wp9-sod-panel"
              className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3"
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldAlert className="h-4 w-4 text-amber-600" />
                {t('adminScreens.permissions.sod')}
              </div>
              {livePairs.map((p) => (
                <div key={p.pairKey} className="flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pairLabel(p.pairKey)}</span>
                      <Badge variant={p.acknowledged ? 'success' : 'warning'}>
                        {p.acknowledged
                          ? t('adminScreens.permissions.acknowledged')
                          : t('adminScreens.permissions.refused')}
                      </Badge>
                      {p.unsaved && !p.acknowledged && (
                        <Badge variant="muted">{t('adminScreens.permissions.unsaved')}</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {p.left} + {p.right} — {p.rationale}
                    </p>
                    {p.roleWide && (
                      <p className="text-xs text-muted-foreground">
                        {t('adminScreens.permissions.roleWide')}
                      </p>
                    )}
                  </div>
                  {isOwner &&
                    (p.ack ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        onClick={() => revokeAck(p.ack!.id)}
                      >
                        <Ban className="h-4 w-4" />
                        {t('adminScreens.permissions.revoke')}
                      </Button>
                    ) : !p.acknowledged ? (
                      <Button size="sm" variant="outline" onClick={() => setAckPair(p.pairKey)}>
                        <ShieldCheck className="h-4 w-4" />
                        {t('adminScreens.permissions.acknowledgeEllipsis')}
                      </Button>
                    ) : null)}
                </div>
              ))}
              {ackPair && (
                <div className="space-y-2 rounded-md border bg-background p-3">
                  <p className="text-xs text-muted-foreground">
                    <Trans
                      i18nKey="adminScreens.permissions.ackExplain"
                      values={{ pair: pairLabel(ackPair) }}
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <Input
                    placeholder={t('adminScreens.permissions.ackPlaceholder')}
                    value={ackReason}
                    onChange={(e) => setAckReason(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setAckPair(null);
                        setAckReason('');
                      }}
                    >
                      <X className="h-4 w-4" />
                      {t('adminScreens.permissions.cancel')}
                    </Button>
                    <Button size="sm" disabled={busy} onClick={grantAck}>
                      <ShieldCheck className="h-4 w-4" />{' '}
                      {t('adminScreens.permissions.acknowledge')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ---- the catalogue, by zone --------------------------------------------- */}
          {byZone.map(([zone, entries]) => (
            <div key={zone}>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {zone}
              </div>
              <div className="grid gap-1 sm:grid-cols-2">
                {entries.map((e) => {
                  const on = effective(e.action);
                  const overridden = !!draft[e.action];
                  return (
                    <label
                      key={e.action}
                      title={
                        e.covers.length
                          ? t('adminScreens.permissions.covers', { list: e.covers.join('; ') })
                          : undefined
                      }
                      className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        className={checkboxClass}
                        checked={on}
                        onChange={() => toggle(e.action)}
                      />
                      <span className={on ? '' : 'text-muted-foreground'}>{e.action}</span>
                      {overridden && (
                        <Badge variant={draft[e.action] === 'GRANT' ? 'success' : 'muted'}>
                          {draft[e.action] === 'GRANT'
                            ? t('adminScreens.permissions.granted')
                            : t('adminScreens.permissions.revoked')}
                        </Badge>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between border-t px-5 py-3">
          <p className="text-xs text-muted-foreground">{t('adminScreens.permissions.footer')}</p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              <X className="h-4 w-4" />
              {t('adminScreens.permissions.close')}
            </Button>
            <Button disabled={busy || !dirty} onClick={save}>
              <Save className="h-4 w-4" />
              {busy ? t('adminScreens.permissions.saving') : t('adminScreens.permissions.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
